import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Alert,
  Autocomplete,
  InputAdornment
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { format } from 'date-fns';
import { 
  useCreateCreditDebitTracker, 
  useUpdateCreditDebitTracker 
} from '../hooks/useCreditDebitTracker';
import { useChartOfAccounts } from '../../chart-of-accounts/hooks/useChartOfAccounts';
import { useLocations } from '../hooks/useLocations';
import { 
  CreditDebitTracker, 
  CreditDebitTrackerFormData, 
  AccountType 
} from '../types/credit-debit-tracker.types';

const validationSchema = yup.object({
  type: yup.string().oneOf([AccountType.CREDIT, AccountType.DEBIT]).required('Type is required'),
  date: yup.string().required('Date is required'),
  chartOfAccountId: yup.string().required('Chart of Account is required'),
  locationId: yup.string().required('Location is required'),
  description: yup.string().required('Description is required').min(3, 'Description must be at least 3 characters'),
  amount: yup.number().required('Amount is required').positive('Amount must be positive'),
  referenceNumber: yup.string().required('Reference Number is required'),
  referenceDate: yup.string().required('Reference Date is required'),
  preparedBy: yup.string().required('Prepared By is required'),
  passedBy: yup.string().required('Passed By is required'),
});

interface CreditDebitTrackerFormProps {
  open: boolean;
  onClose: () => void;
  creditDebitTracker?: CreditDebitTracker | null;
  mode: 'add' | 'edit';
}

const CreditDebitTrackerForm: React.FC<CreditDebitTrackerFormProps> = ({
  open,
  onClose,
  creditDebitTracker,
  mode
}) => {
  const [submitError, setSubmitError] = useState<string | null>(null);
  
  const createMutation = useCreateCreditDebitTracker();
  const updateMutation = useUpdateCreditDebitTracker();
  const { chartOfAccounts } = useChartOfAccounts({ journalEntry: true });
  const { locations } = useLocations({ isActive: true });

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
    setValue,
    watch
  } = useForm<CreditDebitTrackerFormData>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      type: AccountType.DEBIT,
      date: format(new Date(), 'yyyy-MM-dd'),
      chartOfAccountId: '',
      locationId: '',
      description: '',
      amount: 0,
      referenceNumber: '',
      referenceDate: '',
      preparedBy: '',
      passedBy: '',
    }
  });

  // Reset form when dialog opens/closes or creditDebitTracker changes
  useEffect(() => {
    if (open) {
      if (mode === 'edit' && creditDebitTracker) {
        reset({
          type: creditDebitTracker.type,
          date: format(new Date(creditDebitTracker.date), 'yyyy-MM-dd'),
          chartOfAccountId: creditDebitTracker.chartOfAccountId,
          locationId: creditDebitTracker.locationId || '',
          description: creditDebitTracker.description,
          amount: creditDebitTracker.amount,
          referenceNumber: creditDebitTracker.referenceNumber || '',
          referenceDate: creditDebitTracker.referenceDate 
            ? format(new Date(creditDebitTracker.referenceDate), 'yyyy-MM-dd') 
            : '',
          preparedBy: creditDebitTracker.preparedBy || '',
          passedBy: creditDebitTracker.passedBy || '',
        });
      } else {
        reset({
          type: AccountType.DEBIT,
          date: format(new Date(), 'yyyy-MM-dd'),
          chartOfAccountId: '',
          locationId: '',
          description: '',
          amount: 0,
          referenceNumber: '',
          referenceDate: '',
          preparedBy: '',
          passedBy: '',
        });
      }
      setSubmitError(null);
    }
  }, [open, mode, creditDebitTracker, reset]);

  const onSubmit = async (data: CreditDebitTrackerFormData) => {
    try {
      setSubmitError(null);
      
      // Convert and validate amount
      const amountValue = parseFloat(data.amount.toString());
      if (isNaN(amountValue) || amountValue <= 0) {
        setSubmitError('Please enter a valid amount greater than 0');
        return;
      }

      const submitData = {
        ...data,
        amount: amountValue.toFixed(2), // Send as decimal string
        createdBy: mode === 'add' ? 'current-user' : undefined,
        updatedBy: mode === 'edit' ? 'current-user' : undefined,
      } as any;

      // Debug: Log the data being sent
      console.log('Submitting data:', submitData);
      console.log('Amount type:', typeof submitData.amount);
      console.log('Amount value:', submitData.amount);

      if (mode === 'add') {
        await createMutation.mutateAsync(submitData);
      } else if (mode === 'edit' && creditDebitTracker) {
        await updateMutation.mutateAsync({
          id: creditDebitTracker.id,
          data: submitData
        });
      }
      
      onClose();
    } catch (error: any) {
      console.error('Failed to save transaction:', error);
      console.error('Error response:', error?.response?.data);
      setSubmitError(error?.response?.data?.message || error?.response?.data?.errors?.join(', ') || 'Failed to save transaction. Please try again.');
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  const selectedAccount = chartOfAccounts.find(account => account.id === watch('chartOfAccountId'));

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '600px' }
      }}
    >
      <DialogTitle>
        {mode === 'add' ? 'Add New Transaction' : 'Edit Transaction'}
      </DialogTitle>
      
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          {submitError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {submitError}
            </Alert>
          )}

          <Grid container spacing={3}>
            {/* Transaction Type */}
            <Grid item xs={12} md={6}>
              <Controller
                name="type"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.type}>
                    <InputLabel>Transaction Type *</InputLabel>
                    <Select
                      {...field}
                      label="Transaction Type *"
                    >
                      <MenuItem value={AccountType.CREDIT}>Credit</MenuItem>
                      <MenuItem value={AccountType.DEBIT}>Debit</MenuItem>
                    </Select>
                    {errors.type && (
                      <Typography variant="caption" color="error">
                        {errors.type.message}
                      </Typography>
                    )}
                  </FormControl>
                )}
              />
            </Grid>

            {/* Date */}
            <Grid item xs={12} md={6}>
              <Controller
                name="date"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    type="date"
                    label="Transaction Date *"
                    error={!!errors.date}
                    helperText={errors.date?.message}
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
            </Grid>

            {/* Chart of Account */}
            <Grid item xs={12}>
              <Controller
                name="chartOfAccountId"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <Autocomplete
                    value={chartOfAccounts.find(account => account.id === value) || null}
                    onChange={(_event, newValue) => {
                      onChange(newValue?.id || '');
                    }}
                    options={chartOfAccounts}
                    getOptionLabel={(option) => `${option.accountCode} - ${option.name}`}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Chart of Account *"
                        error={!!errors.chartOfAccountId}
                        helperText={errors.chartOfAccountId?.message}
                      />
                    )}
                    renderOption={(props, option) => (
                      <Box component="li" {...props}>
                        <Box>
                          <Typography variant="body2">
                            {option.accountCode} - {option.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Type: {option.type}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  />
                )}
              />
              {selectedAccount && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Account Type: {selectedAccount.type}
                </Typography>
              )}
            </Grid>

            {/* Location */}
            <Grid item xs={12}>
              <Controller
                name="locationId"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <Autocomplete
                    value={locations.find(location => location.id === value) || null}
                    onChange={(_event, newValue) => {
                      onChange(newValue?.id || '');
                    }}
                    options={locations}
                    getOptionLabel={(option) => `${option.code} - ${option.name}`}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Location *"
                        error={!!errors.locationId}
                        helperText={errors.locationId?.message}
                      />
                    )}
                    renderOption={(props, option) => (
                      <Box component="li" {...props}>
                        <Box>
                          <Typography variant="body2">
                            {option.code} - {option.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Type: {option.locationType} | Facility: {option.facility?.name}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  />
                )}
              />
            </Grid>

            {/* Description */}
            <Grid item xs={12}>
              <Controller
                name="description"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    multiline
                    rows={3}
                    label="Description *"
                    error={!!errors.description}
                    helperText={errors.description?.message}
                    placeholder="Enter transaction description..."
                  />
                )}
              />
            </Grid>

            {/* Amount */}
            <Grid item xs={12} md={6}>
              <Controller
                name="amount"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    type="number"
                    label="Amount *"
                    error={!!errors.amount}
                    helperText={errors.amount?.message}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                    }}
                    inputProps={{
                      min: 0,
                      step: 0.01
                    }}
                  />
                )}
              />
            </Grid>

            {/* Reference Number */}
            <Grid item xs={12} md={6}>
              <Controller
                name="referenceNumber"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Reference Number *"
                    error={!!errors.referenceNumber}
                    helperText={errors.referenceNumber?.message}
                    placeholder="Enter reference number..."
                  />
                )}
              />
            </Grid>

            {/* Reference Date */}
            <Grid item xs={12} md={6}>
              <Controller
                name="referenceDate"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    type="date"
                    label="Reference Date *"
                    error={!!errors.referenceDate}
                    helperText={errors.referenceDate?.message}
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
            </Grid>

            {/* Prepared By */}
            <Grid item xs={12} md={6}>
              <Controller
                name="preparedBy"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Prepared By *"
                    error={!!errors.preparedBy}
                    helperText={errors.preparedBy?.message}
                    placeholder="Enter preparer name..."
                  />
                )}
              />
            </Grid>

            {/* Passed By */}
            <Grid item xs={12} md={6}>
              <Controller
                name="passedBy"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Passed By *"
                    error={!!errors.passedBy}
                    helperText={errors.passedBy?.message}
                    placeholder="Enter approver name..."
                  />
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 3 }}>
          <Button 
            onClick={handleClose} 
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting}
          >
            {isSubmitting 
              ? (mode === 'add' ? 'Adding...' : 'Updating...') 
              : (mode === 'add' ? 'Add Transaction' : 'Update Transaction')
            }
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default CreditDebitTrackerForm;
